import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { RadioRating } from "~/common/components/ui/RadioRating";
import { CeroToFive } from "~/common/utils";
import {
  CeroToFiveQuestionData,
  CompletedCeroToFiveAnswer,
  ConsultationRadioAnswer,
} from "~/common/utils/consultations/shared";
import { ConsultationQuestionsLayout } from "~/patients/components";
import { createRadioButtonSchema } from "~/patients/utils";

export interface CeroToFiveFormProps {
  question: CeroToFiveQuestionData;
  handleGoBack: () => void;
  onSubmit: (answer: ConsultationRadioAnswer) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  completedAnswer: CompletedCeroToFiveAnswer;
}

export const CeroToFiveForm = ({
  question,
  handleGoBack,
  onSubmit,
  isSubmitting,
  disableBack,
  completedAnswer,
}: CeroToFiveFormProps) => {
  const {
    options,
    title,
    supertitle,
    subtitle,
    highestRateLevel,
    lowerRateLevel,
  } = question;
  const schema = createRadioButtonSchema(options);

  type CeroToFiveFormValues = z.infer<typeof schema>;
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CeroToFiveFormValues>({
    defaultValues: { answer: completedAnswer?.value },
    resolver: zodResolver(schema),
  });

  const saveAnswers: SubmitHandler<CeroToFiveFormValues> = (data) => {
    const input: ConsultationRadioAnswer = {
      answer: { value: data.answer },
    };
    onSubmit(input);
  };

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      disableNext={!watch("answer")}
      errors={errors.answer?.message}
      isSubmitting={isSubmitting}
      title={title}
      subtitle={subtitle}
      supertitle={supertitle}
      disableBack={disableBack}
    >
      <Controller
        control={control}
        name="answer"
        render={({ field, fieldState }) => (
          <RadioRating
            options={CeroToFive.map((option) => ({
              value: option,
              label: option,
            }))}
            lowerRateLevel={lowerRateLevel}
            highestRateLevel={highestRateLevel}
            error={fieldState.error?.message}
            {...field}
          />
        )}
      />
    </ConsultationQuestionsLayout>
  );
};
